import { Loader2 } from 'lucide-react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext.js';
import { AppProvider } from './contexts/AppContext.js';
import { ServicesProvider } from './contexts/ServicesContext.js';
import { ThemeProvider } from './contexts/ThemeContext.js';
import { useInitializeContacts } from './hooks/useInitializeContacts.js';
import { LoginPage } from './components/auth/LoginPage.js';
import { AppLayout } from './layouts/AppLayout.js';
import { WelcomePage } from './pages/WelcomePage.js';
import { ChatPage } from './pages/ChatPage.js';
import { NotesPage } from './pages/NotesPage.js';
import { NoteEditorPage } from './pages/NoteEditorPage.js';
import { MemoriesPage } from './pages/MemoriesPage.js';

// Define routes
const router = createBrowserRouter([
  {
    path: '/',
    element: <AppLayout />,
    children: [
      {
        index: true,
        element: <WelcomePage />,
      },
      {
        path: 'chat',
        children: [
          {
            index: true,
            element: <Navigate to="/" replace />,
          },
          {
            path: 'new',
            element: <ChatPage />,
          },
          {
            path: ':sessionId',
            element: <ChatPage />,
          },
        ],
      },
      {
        path: 'notes',
        children: [
          {
            index: true,
            element: <NotesPage />,
          },
          {
            path: 'editor',
            element: <NoteEditorPage />,
          },
        ],
      },
      {
        path: 'memories',
        element: <MemoriesPage />,
      },
    ],
  },
]);

function AppContent() {
  const { isAuthenticated, loading } = useAuth();

  // Initialize sample contacts on first load
  useInitializeContacts();

  // Loading state
  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - show login page
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Authenticated - show app with router
  return (
    <AppProvider>
      <ServicesProvider>
        <RouterProvider router={router} />
      </ServicesProvider>
    </AppProvider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export default App;
